import { useThree } from '@react-three/fiber'
import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { RolodexTilesPlugin } from 'features/overview'
import { useSiteModelContext } from 'features/siteMap/contexts/SiteModelContext'
import { useTilesContext } from 'features/siteMap/contexts/TilesContext'
import {
  CompassGizmo,
  GLTFExtensionsPlugin,
  ReorientationPlugin,
  TilesFadePlugin,
  TilesPluginR3f,
  TilesRenderer,
  TilesRendererR3f,
} from 'gecko-3d-tiles-renderer'
import React, { useMemo } from 'react'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'
const dracoLoader = new DRACOLoader().setDecoderPath(
  'https://www.gstatic.com/draco/versioned/decoders/1.5.7/'
)

export const TilesRendererWrapper: React.FC = () => {
  const { setTilesMatrix, setTilesEllipsiod } = useTilesContext()
  const { siteModel } = useSiteModelContext()

  const tilesRendererRef = React.useRef<TilesRenderer | null>(null)

  const size = useThree((state) => state.size)

  const { authorizedAxios } = useGeckoAuth()
  const { ROLODEX_URL } = useRuntimeVariables()

  const compassMargin: [number, number] = React.useMemo(
    () => [25, size.height - 50],
    [size]
  )

  const rolodexPluginOptions = useMemo(() => {
    return {
      entryID: siteModel.id,
      rootPath: 'vricon_3dtiles',
      axios: authorizedAxios,
      rolodexURL: ROLODEX_URL,
    }
  }, [siteModel, authorizedAxios, ROLODEX_URL])

  const onTilesLoad = () => {
    const tilesEllipsoid = tilesRendererRef.current?.ellipsoid
    const tilesFrame = tilesRendererRef.current?.group.matrixWorld
    if (tilesEllipsoid && tilesFrame) {
      setTilesEllipsiod(tilesEllipsoid)
      setTilesMatrix(tilesFrame)
      tilesRendererRef.current?.removeEventListener(
        'tiles-load-end',
        onTilesLoad
      )
    }
  }

  return (
    <TilesRendererR3f
      url={'tileset.json'}
      errorTarget={1}
      ref={(ref) => {
        tilesRendererRef.current = ref
        ref?.addEventListener('tiles-load-end', onTilesLoad)
      }}>
      <TilesPluginR3f plugin={RolodexTilesPlugin} args={rolodexPluginOptions} />
      <TilesPluginR3f plugin={TilesFadePlugin} fadeDuration={500} />
      <TilesPluginR3f plugin={ReorientationPlugin} />
      <TilesPluginR3f plugin={GLTFExtensionsPlugin} dracoLoader={dracoLoader} />
      <CompassGizmo margin={compassMargin} />
    </TilesRendererR3f>
  )
}
